import React, { useState, useRef, useEffect } from 'react';
import { X, Star, Clock } from 'lucide-react';
import { startProcessing, starEmail, snoozeEmail } from '../utils/api';
import { TIER_CONFIG, normalizeImportance } from '../utils/formatters';

export default function ProcessingView({ emailIds, onComplete, onCancel, snoozeLabelId }) {
  const [current, setCurrent] = useState(0);
  const [total] = useState(emailIds.length);
  const [results, setResults] = useState([]);
  const resultsRef = useRef([]);
  const [status, setStatus] = useState('Processing...');
  const cancelRef = useRef(null);

  const patchLabelsInResults = (messageId, labels) => {
    resultsRef.current = resultsRef.current.map((r) =>
      r.email?.id === messageId ? { ...r, email: { ...r.email, labels } } : r
    );
    setResults([...resultsRef.current]);
  };

  const handleQuickStar = async (e, messageId) => {
    e.stopPropagation();
    const r = resultsRef.current.find((x) => x.email?.id === messageId);
    const starred = r?.email?.labels?.includes('STARRED');
    try {
      const out = await starEmail(messageId, !starred);
      if (out.success) patchLabelsInResults(messageId, out.labels);
    } catch (err) {
      console.error(err);
    }
  };

  const handleQuickSnooze = async (e, messageId) => {
    e.stopPropagation();
    const r = resultsRef.current.find((x) => x.email?.id === messageId);
    const labels = r?.email?.labels || [];
    const isSnoozed = snoozeLabelId && labels.includes(snoozeLabelId);
    try {
      const out = await snoozeEmail(messageId, !isSnoozed);
      if (out.success) patchLabelsInResults(messageId, out.labels);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    console.log('[ProcessingView] Effect started for', emailIds.length, 'emails');
    const cancel = startProcessing(emailIds, {
      onProgress: (data) => {
        setCurrent(data.current);
        console.log('[ProcessingView] Progress:', data.current, '/', data.total);
        setResults((prev) => {
          const next = [...prev, data.result];
          resultsRef.current = next;
          return next;
        });
        setStatus(`Analyzing email ${data.current} of ${data.total}...`);
      },
      onComplete: () => {
        console.log('[ProcessingView] onComplete called');
        setStatus('Complete!');
        setTimeout(() => {
          onComplete(resultsRef.current);
        }, 500);
      },
      onError: (msg) => {
        console.error('[ProcessingView] onError:', msg);
        setStatus(`Error: ${msg}`);
      },
    });
    cancelRef.current = cancel;

    return () => {
      if (cancelRef.current) cancelRef.current();
    };
  }, []);

  const handleCancel = () => {
    if (cancelRef.current) cancelRef.current();
    onCancel(resultsRef.current);
  };

  const progress = total > 0 ? current / total : 0;
  const circumference = 2 * Math.PI * 72;
  const offset = circumference - progress * circumference;

  return (
    <div className="processing-view">
      <div className="scanline-overlay" />

      <div className="progress-ring-container">
        <svg className="progress-ring" width="180" height="180" viewBox="0 0 180 180">
          <defs>
            <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#00f0ff" />
              <stop offset="100%" stopColor="#a855f7" />
            </linearGradient>
          </defs>
          <circle className="progress-ring-bg" cx="90" cy="90" r="72" />
          <circle
            className="progress-ring-fill"
            cx="90"
            cy="90"
            r="72"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <div className="progress-count">
          <div className="progress-count-num">
            {current}/{total}
          </div>
          <div className="progress-count-label">emails analyzed</div>
        </div>
      </div>

      <div className="processing-status">{status}</div>

      <p style={{ color: 'var(--text-dim)', fontSize: '0.85rem', maxWidth: 420, textAlign: 'center', marginBottom: 8 }}>
        Star or snooze messages as each result appears — changes sync to Gmail.
      </p>

      <button type="button" className="btn btn-danger" onClick={handleCancel}>
        <X size={16} /> Cancel Processing
      </button>

      {results.length > 0 && (
        <div className="processing-feed">
          {results
            .slice(-8)
            .reverse()
            .map((r) => {
              const tier = normalizeImportance(r.importance);
              const Icon = TIER_CONFIG[tier]?.icon || TIER_CONFIG.routine.icon;
              const id = r.email?.id;
              const lbl = r.email?.labels || [];
              const starOn = lbl.includes('STARRED');
              const snoozeOn = snoozeLabelId && lbl.includes(snoozeLabelId);
              return (
                <div
                  key={id}
                  className="feed-item"
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                >
                  <span style={{ display: 'flex' }}>
                    <Icon size={16} color={TIER_CONFIG[tier]?.color || '#00f0ff'} />
                  </span>
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    <strong>{r.email.sender}</strong> — {r.email.subject}
                  </div>
                  <button
                    type="button"
                    title={starOn ? 'Unstar' : 'Star in Gmail'}
                    onClick={(e) => handleQuickStar(e, id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: starOn ? 'var(--amber)' : 'var(--text-dim)',
                      padding: 4,
                      flexShrink: 0,
                    }}
                  >
                    <Star size={15} fill={starOn ? 'currentColor' : 'none'} />
                  </button>
                  <button
                    type="button"
                    title={snoozeOn ? 'Return to inbox' : 'Snooze'}
                    onClick={(e) => handleQuickSnooze(e, id)}
                    disabled={!snoozeLabelId}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: snoozeLabelId ? 'pointer' : 'wait',
                      color: snoozeOn ? 'var(--cyan)' : 'var(--text-dim)',
                      padding: 4,
                      flexShrink: 0,
                      opacity: snoozeLabelId ? 1 : 0.45,
                    }}
                  >
                    <Clock size={15} />
                  </button>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
